import React from 'react'
import { FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Callout, Checkbox, Combobox, ComboboxItem, Input } from '@node-space/ui-components'
import { SetCustomerFeeFormData } from 'types/customers'
import { FeeStructureTypes } from '../types'

interface CustomerSpecificFeeCardProps {
  currencyOptions: ComboboxItem[]
  feeStructureOptions: ComboboxItem[]
  formValues: SetCustomerFeeFormData
  errors: FieldErrors<SetCustomerFeeFormData>
  register: UseFormRegister<SetCustomerFeeFormData>
  setValue: UseFormSetValue<SetCustomerFeeFormData>
  isLoadingWallets?: boolean
}

export const CustomerSpecificFeeCard = ({
  currencyOptions,
  feeStructureOptions,
  formValues,
  errors,
  register,
  setValue,
  isLoadingWallets = false,
}: CustomerSpecificFeeCardProps) => {
  const { t } = useTranslation()

  const isFixedFeeDisabled = formValues?.feeStructure === FeeStructureTypes.PERCENTAGE
  const isPercentageFeeDisabled = formValues?.feeStructure === FeeStructureTypes.FIXED

  return (
    <Box flexCol gapY={24} width="full">
      <Box flexCol gapY={8}>
        <h5 className="text-sm font-medium text-foreground-prominent">
          {t('customers.feeSetup.applyFeeTo')}
        </h5>
        <Box flex gapX={8} width="full">
          <Box className="flex-1">
            <Checkbox
              {...register('payinsEnabled')}
              id="payins-checkbox"
              name="payins-checkbox"
              testId="payins-checkbox"
              label={t('customers.feeSetup.payins')}
              checked={formValues?.payinsEnabled}
              disabled={formValues?.payoutsEnabled}
              onCheckedChange={checked => setValue('payinsEnabled', Boolean(checked))}
            />
          </Box>
          <Box flex paddingR={20} centerChildren className="flex-1 justify-start">
            <Checkbox
              {...register('payoutsEnabled')}
              id="payouts-checkbox"
              name="payouts-checkbox"
              testId="payouts-checkbox"
              label={t('customers.feeSetup.payouts')}
              checked={formValues?.payoutsEnabled}
              disabled={formValues?.payinsEnabled}
              onCheckedChange={checked => setValue('payoutsEnabled', Boolean(checked))}
            />
          </Box>
        </Box>
      </Box>

      <Box flex gapX={8} width="full">
        <Box className="flex-1">
          <Combobox
            {...register('currency')}
            id="fee-currency"
            selectPlaceholder={isLoadingWallets ? t('loading') : t('select')}
            items={currencyOptions}
            label={t('customers.feeSetup.feeCurrency')}
            testId="fee-currency-combobox"
            onChange={value => setValue('currency', value)}
            defaultValue={formValues?.currency}
            errorText={errors?.currency?.message}
            disabled={isLoadingWallets}
          />
        </Box>
        <Box className="flex-1">
          <Combobox
            {...register('feeStructure')}
            id="fee-structure"
            selectPlaceholder={t('select')}
            items={feeStructureOptions}
            label={t('customers.feeSetup.structure')}
            testId="fee-structure-combobox"
            onChange={value => setValue('feeStructure', value)}
            defaultValue={formValues?.feeStructure}
            errorText={errors?.feeStructure?.message}
          />
        </Box>
        <Box className="flex-1" paddingT={24}>
          <Input
            {...register('fixedFee')}
            disabled={isFixedFeeDisabled}
            placeholderText="0.00"
            rightSuffixText={formValues.currency}
            value={formValues.fixedFee}
            errorText={errors.fixedFee?.message}
            onChange={e => setValue('fixedFee', e?.target?.value)}
          />
        </Box>
        <Box className="flex-1" paddingT={24}>
          <Input
            {...register('percentageFee')}
            disabled={isPercentageFeeDisabled}
            placeholderText="0.00"
            rightSuffixText="%"
            value={formValues?.percentageFee}
            errorText={errors?.percentageFee?.message}
            onChange={e => setValue('percentageFee', e?.target?.value)}
          />
        </Box>
      </Box>
      <Callout variant="info">{t('customers.feeSetup.currencyCallout')}</Callout>
    </Box>
  )
}
